import {
  getPushCampaignErrorCode,
  getPushQuietHoursNextAllowedAt,
  type PushAudienceFilters,
  type PushAudienceJson,
  type PushAudienceType,
  type PushCampaign,
  type PushCampaignErrorCode,
  type PushCampaignPayload,
  type PushEstimateRequest,
  type PushOrderCount,
  type PushOrderState,
} from '../services/resortAdminApi';

export const PUSH_TITLE_MAX_LENGTH = 60;

export const PUSH_BODY_MAX_LENGTH = 180;

export const PUSH_PICTURE_LINK_MAX_LENGTH = 500;

export const PUSH_CAMPAIGN_NAME_MAX_LENGTH = 140;

export const PUSH_DUPLICATE_SUFFIX = '(copy)';

export const PUSH_TIMEZONE = 'Europe/Paris';

export const PUSH_TIMEZONE_NOTICE = 'The time is set in Europe/Paris.';

export const PUSH_QUIET_HOURS_START = '21:00';

export const PUSH_QUIET_HOURS_END = '09:00';

export const PUSH_QUIET_HOURS_NOTICE =
  `Marketing pushes are not delivered between ${PUSH_QUIET_HOURS_START} and ${PUSH_QUIET_HOURS_END} ${PUSH_TIMEZONE}. `
  + 'A campaign that starts inside that window is paused and resumes afterwards.';

export const PUSH_IOS_PICTURE_NOTICE =
  'On iOS the picture is not displayed yet: the push arrives as text only. Android shows the picture.';

export const PUSH_CAMPAIGN_ERROR_MESSAGES: Record<PushCampaignErrorCode, string> = {
  insufficient_role: 'Your role is not allowed to perform this action.',
  field_not_writable: 'The request contained a field that cannot be changed. Reload the page and try again.',
  campaign_not_editable: 'The campaign is no longer a draft and cannot be edited.',
  invalid_status_transition: 'The campaign status changed. Reload the page to see the current state.',
  timestamp_mismatch: 'The campaign was changed elsewhere. Reload the data before saving again.',
  content_invalid: 'The campaign content is invalid. Check the titles and bodies for length and unsupported characters.',
  invalid_picture_link: 'The picture link is invalid. Use a plain https link without credentials.',
  test_email_not_allowlisted: 'One of the test emails is not in the internal test list.',
  test_send_rate_limited: 'Test sending is rate limited. Wait a minute and try again.',
  test_send_required: 'Send a test of the current content before launching.',
  not_confirmed: 'Confirmation is required before launching.',
  recipient_count_drift: 'The audience size changed since you confirmed. Reopen the dialog and confirm the new number.',
  recipient_limit_exceeded: 'The audience is larger than the configured recipient limit.',
  empty_audience: 'The audience is empty, there is nobody to send to.',
  firebase_unavailable: 'Firebase credentials are not active. Sending is not possible right now.',
  scheduled_in_past: 'The scheduled time is in the past. Pick a time in the future.',
  another_campaign_running: 'Another campaign is already sending. Wait until it finishes.',
  order_filter_not_applicable: 'The order filter is not available for this audience.',
  invalid_audience_filter: 'One of the audience filters holds a value the server does not accept. Reload the page and set the filters again.',
  estimate_timeout: 'The audience estimate timed out. Try again in a moment.',
  estimate_in_progress: 'Another estimate is still running. Try again in a moment.',
  estimate_rate_limited: 'Too many audience estimates in a row. Wait a minute and try again.',
  quiet_hours: `Sending is not allowed during the quiet hours, ${PUSH_QUIET_HOURS_START} to ${PUSH_QUIET_HOURS_END} ${PUSH_TIMEZONE}.`,
  push_campaigns_disabled: 'Push campaigns are disabled in the settings.',
  unsafe_rate_config: 'The sending rate configuration is unsafe. Ask an administrator to review the settings.',
  campaign_not_found: 'The campaign was not found.',
};

export const PUSH_ORDER_STATES: PushOrderState[] = [
  'any',
  'never_ordered',
  'ordered_paid',
  'unpaid_only',
  'dormant',
];

export const PUSH_ORDER_COUNTS: PushOrderCount[] = ['any', '0', '1', '2+'];

export const PUSH_LANGUAGE_OPTIONS: { value: string; label: string; }[] = [
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'French' },
];

export const PUSH_PLATFORM_OPTIONS: { value: string; label: string; }[] = [
  { value: 'iOS', label: 'iOS' },
  { value: 'Android', label: 'Android' },
];

export const EMPTY_PUSH_AUDIENCE_FILTERS: PushAudienceFilters = {
  order_state: 'any',
  order_state_days: null,
  resorts: [],
  languages: [],
  platforms: [],
  not_seen_for_days: null,
  registered_from: null,
  registered_to: null,
  order_count: 'any',
};

export function readPushAudienceFilters(audienceJson: unknown): PushAudienceFilters {
  if (audienceJson == null || audienceJson === '') {
    return { ...EMPTY_PUSH_AUDIENCE_FILTERS };
  }
  let source: unknown = audienceJson;
  if (typeof source === 'string') {
    try {
      source = JSON.parse(source);
    } catch {
      throw new Error('The saved audience filter could not be read. Reload the page, and do not send until it shows correctly.');
    }
  }
  const raw = (source ?? {}) as Record<string, unknown>;
  return {
    order_state: readEnum(raw.order_state, PUSH_ORDER_STATES, 'any'),
    order_state_days: readPositiveInteger(raw.order_state_days),
    resorts: readStringList(raw.resorts),
    languages: readStringList(raw.languages),
    platforms: readStringList(raw.platforms),
    not_seen_for_days: readPositiveInteger(raw.not_seen_for_days),
    registered_from: readDate(raw.registered_from),
    registered_to: readDate(raw.registered_to),
    order_count: readEnum(raw.order_count, PUSH_ORDER_COUNTS, 'any'),
  };
}

export function toPushAudienceJson(
  audienceType: PushAudienceType,
  filters: PushAudienceFilters
): PushAudienceJson {
  const normalized = normalizePushAudienceFilters(audienceType, filters);
  if (audienceType === 'app_users') {
    return normalized;
  }
  return { resorts: normalized.resorts };
}

export function toPushEstimateRequest(
  audienceType: PushAudienceType,
  filters: PushAudienceFilters,
  campaignName: string | null
): PushEstimateRequest {
  const normalized = normalizePushAudienceFilters(audienceType, filters);
  return {
    audience_type: audienceType,
    ...normalized,
    campaign_name: campaignName,
  };
}

export function normalizePushAudienceFilters(
  audienceType: PushAudienceType,
  filters: PushAudienceFilters
): PushAudienceFilters {
  if (audienceType !== 'app_users') {
    return { ...EMPTY_PUSH_AUDIENCE_FILTERS, resorts: filters.resorts };
  }
  return {
    ...filters,
    order_state_days: filters.order_state === 'dormant' ? filters.order_state_days : null,
  };
}

export function toPushCampaignDuplicatePayload(campaign: PushCampaign): PushCampaignPayload {
  return {
    campaign_name: buildDuplicateName(campaign.campaign_name ?? ''),
    audience_type: campaign.audience_type,
    audience_json: toPushAudienceJson(campaign.audience_type, readPushAudienceFilters(campaign.audience_json)),
    test_emails: campaign.test_emails ?? [],
    title_en: campaign.title_en ?? '',
    title_fr: campaign.title_fr ?? '',
    body_en: campaign.body_en ?? '',
    body_fr: campaign.body_fr ?? '',
    picture_link: campaign.picture_link ?? '',
    send_mode: 'now',
    scheduled_at: null,
  };
}

function buildDuplicateName(value: string): string {
  const base = value.trim() === '' ? 'Push campaign' : value.trim();
  return `${base} ${PUSH_DUPLICATE_SUFFIX}`.slice(0, PUSH_CAMPAIGN_NAME_MAX_LENGTH);
}

export function validatePushAudienceFilters(
  audienceType: PushAudienceType,
  filters: PushAudienceFilters
): string[] {
  if (audienceType !== 'app_users') {
    return [];
  }
  const errors: string[] = [];
  if (filters.order_state === 'dormant' && filters.order_state_days == null) {
    errors.push('Set the number of days for the dormant filter.');
  }
  if (filters.registered_from != null && filters.registered_to != null && filters.registered_from > filters.registered_to) {
    errors.push('The registration period starts after it ends.');
  }
  return errors;
}

function readEnum<T extends string>(value: unknown, allowed: T[], fallback: T): T {
  if (value == null || value === '') {
    return fallback;
  }
  const known = allowed.find(x => x === value);
  if (known == null) {
    throw new Error('The saved audience filter holds an unknown value. Reload the page, and do not send until it shows correctly.');
  }
  return known;
}

function readPositiveInteger(value: unknown): number | null {
  if (value == null || value === '') {
    return null;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return null;
  }
  return Math.trunc(parsed);
}

function readStringList(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(x => typeof x === 'string' && x !== '');
}

function readDate(value: unknown): string | null {
  if (typeof value !== 'string' || value === '') {
    return null;
  }
  return value.slice(0, 10);
}

export function countCodePoints(value: string): number {
  return [...value].length;
}

export function isPushTextTooLong(value: string, maxLength: number): boolean {
  return countCodePoints(value) > maxLength;
}

export function validatePictureLink(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  if (trimmed.length > PUSH_PICTURE_LINK_MAX_LENGTH) {
    return `The picture link must be at most ${PUSH_PICTURE_LINK_MAX_LENGTH} characters.`;
  }
  if (hasUnsafeCharacters(trimmed)) {
    return 'The picture link must not contain spaces or control characters.';
  }
  let parsed: URL | null = null;
  try {
    parsed = new URL(trimmed);
  } catch {
    return 'The picture link must be a valid absolute URL.';
  }
  if (parsed.protocol !== 'https:') {
    return 'The picture link must start with https://.';
  }
  if (parsed.hostname === '') {
    return 'The picture link must have a host.';
  }
  if (parsed.username !== '' || parsed.password !== '') {
    return 'The picture link must not contain credentials.';
  }
  return null;
}

function hasUnsafeCharacters(value: string): boolean {
  for (const character of value) {
    const code = character.codePointAt(0) ?? 0;
    if (code <= 0x20 || (code >= 0x7f && code <= 0xa0) || code === 0x2028 || code === 0x2029 || code === 0xfeff) {
      return true;
    }
  }
  return false;
}

export function parseTestEmails(value: string): string[] {
  return value
    .split(/[\s,;]+/)
    .map(x => x.trim().toLowerCase())
    .filter(x => x !== '');
}

export function formatTestEmails(value: string[]): string {
  return value.join(', ');
}

export function validateTestEmails(value: string): string | null {
  const emails = parseTestEmails(value);
  if (emails.length === 0) {
    return 'Add at least one email address.';
  }
  const invalid = emails.find(x => !/^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(x));
  if (invalid != null) {
    return 'One of the addresses is not a valid email.';
  }
  return null;
}

export function validatePushCampaignContent(content: {
  title_en: string;
  body_en: string;
  title_fr: string;
  body_fr: string;
  picture_link: string;
}): string[] {
  const errors: string[] = [];
  if (content.title_en.trim() === '') {
    errors.push('English title is required.');
  }
  if (content.body_en.trim() === '') {
    errors.push('English body is required.');
  }
  const hasFrenchTitle = content.title_fr.trim() !== '';
  const hasFrenchBody = content.body_fr.trim() !== '';
  if (hasFrenchTitle !== hasFrenchBody) {
    errors.push('French title and body must be filled together or both left empty.');
  }
  if (isPushTextTooLong(content.title_en, PUSH_TITLE_MAX_LENGTH)) {
    errors.push(`English title must be at most ${PUSH_TITLE_MAX_LENGTH} characters.`);
  }
  if (isPushTextTooLong(content.title_fr, PUSH_TITLE_MAX_LENGTH)) {
    errors.push(`French title must be at most ${PUSH_TITLE_MAX_LENGTH} characters.`);
  }
  if (isPushTextTooLong(content.body_en, PUSH_BODY_MAX_LENGTH)) {
    errors.push(`English body must be at most ${PUSH_BODY_MAX_LENGTH} characters.`);
  }
  if (isPushTextTooLong(content.body_fr, PUSH_BODY_MAX_LENGTH)) {
    errors.push(`French body must be at most ${PUSH_BODY_MAX_LENGTH} characters.`);
  }
  const pictureError = validatePictureLink(content.picture_link);
  if (pictureError != null) {
    errors.push(pictureError);
  }
  return errors;
}

export function getPushCampaignFailureMessage(error: unknown, fallback: string): string {
  const code = getPushCampaignErrorCode(error);
  if (code === 'quiet_hours') {
    return getPushQuietHoursMessage(getPushQuietHoursNextAllowedAt(error));
  }
  return getPushCampaignErrorMessage(code, fallback);
}

export function getPushCampaignErrorMessage(code: PushCampaignErrorCode | null, fallback: string): string {
  if (code == null) {
    return fallback;
  }
  return PUSH_CAMPAIGN_ERROR_MESSAGES[code] ?? fallback;
}

export function getPushQuietHoursMessage(nextAllowedAt: string | null): string {
  const base = PUSH_CAMPAIGN_ERROR_MESSAGES.quiet_hours;
  const formatted = formatPushDateTime(nextAllowedAt);
  if (formatted == null) {
    return base;
  }
  return `${base} The earliest possible start is ${formatted} ${PUSH_TIMEZONE}.`;
}

export function isInsidePushQuietHours(value: string | null): boolean {
  if (value == null || value === '') {
    return false;
  }
  const time = value.slice(11, 16);
  if (!/^\d{2}:\d{2}$/.test(time)) {
    return false;
  }
  return time >= PUSH_QUIET_HOURS_START || time < PUSH_QUIET_HOURS_END;
}

export function formatPushDateTime(value: string | null): string | null {
  if (value == null || value === '') {
    return null;
  }
  const parsed = new Date(value.replace(' ', 'T'));
  if (isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
